import { LOCALSTORAGE_KEYS } from "../constants/users";
import { getToken } from "../helpers/localStorageCache";
import { bootstrapDevMockSession } from "../helpers/devAutoLogin";
import { shopifyHoldOn } from "../constants/shopifyAppStoreHolds";

// Apply localhost mock tenant before any domain snapshot is taken.
// Otherwise a stale app.riverr.app mockUser wins and the UI thinks
// we're on riverr.app instead of bespokethreads / bigfrog / etc.
bootstrapDevMockSession();

export const currentDomain = (function () {
  return (
    getToken(LOCALSTORAGE_KEYS.mockUser)?.currentDomain ||
    window.location.hostname
  );
})();

export const isHoaxMerchDomain = (function () {
  // return window.location.hostname === "app.hoaxmerch.com";
  return currentDomain === "app.themerchyard.com";
})();

export const isStagingDomain = (function () {
  return currentDomain === "riverr-enterprise-user.web.app";
})();

export const isLocalhost = (function () {
  const host =
    typeof window !== "undefined" ? window.location.hostname : currentDomain;
  return host === "localhost" || host === "127.0.0.1";
})();

export const isDevDomain = (function () {
  return isLocalhost || isStagingDomain;
})();

export const isLarevivalDomain = (function () {
  // return currentDomain === "larevival.myriverr.com";
  return currentDomain === "app.larevivalapparel.com";
})();

export const isGirlTribeCoDomain = (function () {
  return currentDomain === "app.girltribeco.com";
})();

export const isPrintPoetsDomain = (function () {
  return currentDomain === "app.printpoets.com";
})();

export const isRakilineDomain = (function () {
  return currentDomain === "app.rakiline.com";
})();

export const isTeeprintcentreDomain = (function () {
  return currentDomain === "app.teeprintcentre.com.au";
})();

export const isRakilineEuDomain = (function () {
  return currentDomain === "app.rakiline.eu";
})();

export const isPodhutDomain = (function () {
  return currentDomain === "app.podhut.com";
})();

export const isRiverrDomain = (function () {
  return currentDomain === "app.riverr.app";
})();

const RIVERR_PLATFORM_EMAIL = /@(riverr\.app|getriverr\.com)$/i;

export const isRiverrPlatformAdmin = (email) =>
  RIVERR_PLATFORM_EMAIL.test(String(email || ""));

export const isBespokeThreadsDomain = (function () {
  return currentDomain === "app.bespokethreads.com";
})();

export const isTeePrintCenterDomain = (function () {
  return currentDomain === "app.teeprintcentre.com.au";
})();

export const isMerchfillerDomain = (function () {
  return currentDomain === "app.merchfiller.com";
})();

export const isMultiplyInkDomain = (function () {
  return currentDomain === "app.multiplyink.com";
})();

export const isPrintifyDomain = (function () {
  return (
    currentDomain === "printify.myriverr.com" ||
    currentDomain === "locationstest.myriverr.com"
    // currentDomain === "bigfrog.myriverr.com"
  );
})();

export const isPrintOnDemandDomain = (function () {
  return currentDomain === "app.printondemand.com.ph";
})();

export const isMyShirtPodDomain = (function () {
  return currentDomain === "app.myshirtpod.com";
})();

export const isJmDtgPrintingDomain = (function () {
  return currentDomain === "app.jmdtgprinting.net";
})();

export const isDtgShirtsDomain = (function () {
  return currentDomain === "app.dtgtshirts.co.uk";
})();

export const isDropmerchDomain = (function () {
  return currentDomain === "app.dropmerch.co.uk";
})();

export const isBackStoryPrintsDomain = (function () {
  return currentDomain === "app.backstoryprints.com";
})();

export const isLuxtownDomain = (function () {
  return currentDomain === "app.luxtown.com";
})();

export const isAimlessGoodsDomian = (function () {
  return currentDomain === "app.aimlessgoods.com";
})();

export const isJumbodtgDomian = (function () {
  return currentDomain === "app.jumbodtg.com";
})();

export const isTheSublimationStationDomian = (function () {
  return currentDomain === "app.thesublimationstation.com";
})();

export const isPromiseApparelDomian = (function () {
  return currentDomain === "app.promiseapparel.com";
})();

export const isDallasPrintHouseDomian = (function () {
  return currentDomain === "app.dallasprinthouse.com";
})();

export const isThinkProDomian = (function () {
  return currentDomain === "app.think.pro";
})();

export const isPrintDropDomian = (function () {
  return currentDomain === "app.printdrop.cl";
})();

export const isTopBananaDomian = (function () {
  return currentDomain === "app.topbananausa.com";
})();

export const isCandlebuildersDomian = (function () {
  return currentDomain === "app.candlebuilders.com";
})();

export const isMerchmonkiesDomian = (function () {
  return currentDomain === "app.merchmonkies.com";
})();

export const isBigfrogDomian = (function () {
  return currentDomain === "bigfrog.myriverr.com";
})();

/** OmniCommerce / Fabritech — App Store install only (no shop-URL connect). */
export const OMNICOMMERCE_ENTERPRISE_ID = "ZrinGQi7xo885EYe1Ti8";
export const OMNICOMMERCE_APP_STORE_URL =
  "https://apps.shopify.com/omnicommerce-2";

export const isOmniCommerceDomain = (function () {
  return (
    currentDomain === "omnicommerce.myriverr.com" ||
    currentDomain === "fabritech.myriverr.com"
  );
})();

export const isOmniCommerceEnterprise = (enterprise) => {
  const entId = enterprise?.id || enterprise?.enterpriseId;
  return (
    entId === OMNICOMMERCE_ENTERPRISE_ID ||
    String(enterprise?.slug || "").toLowerCase() === "omnicommerce"
  );
};

export const isOmniCommerceContext = (enterprise) =>
  isOmniCommerceDomain || isOmniCommerceEnterprise(enterprise);

/** Shopify channel orders (platformId 2). MyCart and other shops stay out. */
export const isShopifyPlatformOrder = (order = {}) =>
  String(order?.platformId ?? "") === "2";

/** Dummy shipment so App Store reviewers never have to fill the form. */
export const OMNI_SHOPIFY_SANDBOX_SHIPMENT = {
  trackingCode: "1Z999AA10123456784",
  carrierName: "UPS",
  carrierService: "Ground",
  shipmentCost: 8,
  trackingUrl: "https://www.ups.com/track?tracknum=1Z999AA10123456784",
};

export const OMNI_SHOPIFY_SANDBOX_AUTO_SHIP_MS = 60 * 1000;

/**
 * OmniCommerce App Store 2.1.1 review sandbox (dummy-tracking Mark Shipped).
 * Off since approval so real merchants never get dummy tracking.
 */
export const OMNI_SHOPIFY_SANDBOX_ENABLED = false;

export const canUseOmniShopifyFulfillmentSandbox = (enterprise, order) =>
  OMNI_SHOPIFY_SANDBOX_ENABLED &&
  isOmniCommerceContext(enterprise) &&
  isShopifyPlatformOrder(order);

/** Candle Builders enterprise (scent-based blanks; also accessed via mock on other domains). */
export const CANDLE_BUILDERS_ENTERPRISE_ID = "GeWigCwLWseMchZkoa3S";

/** Big Frog network HQ enterprise id (parent of franchise child enterprises). */
export const BIG_FROG_ENTERPRISE_ID = "6duhvdsbbxEp2BgNm90f";

/** True when the signed-in (or mocked) enterprise is Candle Builders. */
export const isCandleBuildersEnterprise = (enterprise) => {
  const entId = enterprise?.id || enterprise?.enterpriseId;
  return entId === CANDLE_BUILDERS_ENTERPRISE_ID;
};

/** Candle Builders UI: native domain or accessed via admin mock on another host. */
export const isCandleBuildersContext = (enterprise) =>
  isCandlebuildersDomian || isCandleBuildersEnterprise(enterprise);

/** True when the signed-in enterprise is Big Frog HQ or a Big Frog franchise. */
export const isBigFrogEnterprise = (enterprise) => {
  const entId = enterprise?.id || enterprise?.enterpriseId;
  return (
    entId === BIG_FROG_ENTERPRISE_ID ||
    enterprise?.parentEnterpriseId === BIG_FROG_ENTERPRISE_ID
  );
};

/**
 * Big Frog / network HQ operator (super_admin, or HQ with no location).
 * Franchise location logins are excluded. These users should use Storefront
 * analytics — HQ production rollups do not reflect shop.bigfrog.com GMV.
 */
export const isBigFrogHqSuperAdmin = (enterprise, currentUser) => {
  if (!currentUser?.isEnterpriseAdmin) return false;
  const entId = enterprise?.id || enterprise?.enterpriseId;
  const isNetworkParent =
    !enterprise?.parentEnterpriseId &&
    (enterprise?.paymentSplitModel === "network" ||
      entId === BIG_FROG_ENTERPRISE_ID ||
      isBigfrogDomian);
  if (!isNetworkParent) return false;
  const roles = Array.isArray(currentUser?.roles) ? currentUser.roles : [];
  return roles.includes("super_admin") || !currentUser?.locationId;
};

/** Child enterprise under a network/franchise parent (e.g. Big Frog locations). */
export const isNetworkFranchiseEnterprise = (enterprise) =>
  !!enterprise?.parentEnterpriseId;

/**
 * Any enterprise participating in a network/franchise split: the network HQ
 * (`paymentSplitModel === "network"`) or one of its child franchises
 * (`parentEnterpriseId` set).
 *
 * Domain-independent on purpose — the enterprise doc is loaded by the user's
 * `enterpriseId`, so this resolves correctly whether the user is mocked from
 * the HQ domain or logged in on their own franchise hostname. Use this instead
 * of the exact `isBigfrogDomian` host check for franchise gating.
 */
export const isNetworkFranchiseMember = (enterprise) =>
  enterprise?.paymentSplitModel === "network" ||
  isNetworkFranchiseEnterprise(enterprise);

/**
 * Whether the "add a payment method" alert/redirect should be suppressed.
 * Network franchise members settle through the split model and never add a
 * card; the per-enterprise `hidePaymentMethodAlert` flag and the legacy Big
 * Frog domain check are kept as fallbacks. Per-seller `billingDisabled`
 * (Users list Billing toggle) also waives the card so they can create
 * products and submit orders while invoices stay manual.
 */
/**
 * Shopify App Store 1.2.1 — hide Riverr Stripe app charges and Connect
 * payouts. OmniCommerce is listed as Free; reviewers treat AI credit packs,
 * SaaS plans, and popup-store bank payouts as off-platform app billing.
 * Fulfillment / production invoices (cost of goods) stay visible.
 *
 * Toggle via `SHOPIFY_APP_STORE_HOLDS` in constants/shopifyAppStoreHolds.js.
 */
export const shouldHideOffPlatformAppBilling = (enterprise) => {
  if (!isOmniCommerceContext(enterprise)) return false;
  return (
    shopifyHoldOn("saasPricingAndSubscriptions") ||
    shopifyHoldOn("aiCreditPurchase") ||
    shopifyHoldOn("connectPayouts") ||
    shopifyHoldOn("paymentMethodPrompts")
  );
};

export const shouldHideAiCreditPurchase = (enterprise) =>
  isOmniCommerceContext(enterprise) && shopifyHoldOn("aiCreditPurchase");

/**
 * Natalie Leaman (America First / America250) builds merch drops for her
 * Popup Store. Her account is the only OmniCommerce user who sees the bulk
 * creator while the App Store hold is on. Everyone else stays hidden.
 */
const BULK_PRODUCT_MAKER_ALLOW_EMAILS = ["nleaman@americafirstpolicy.com"];

export const shouldHideBulkProductMaker = (enterprise) => {
  if (!isOmniCommerceContext(enterprise) || !shopifyHoldOn("bulkProductMaker")) {
    return false;
  }
  try {
    const email = String(
      require("../store").default.getState()?.currentUser?.user?.email || "",
    )
      .trim()
      .toLowerCase();
    if (BULK_PRODUCT_MAKER_ALLOW_EMAILS.includes(email)) return false;
  } catch (e) {
    /* store unavailable (tests, SSR): keep it hidden */
  }
  return true;
};

/** Upload + Use AI on Edit details. OmniCommerce is a free App Store listing. */
export const shouldHideCustomMockups = (enterprise) =>
  isOmniCommerceContext(enterprise) && shopifyHoldOn("customMockups");

export const shouldHideConnectPayouts = (enterprise) =>
  isOmniCommerceContext(enterprise) && shopifyHoldOn("connectPayouts");

export const shouldHideOmniEmbroidery = (enterprise) =>
  isOmniCommerceContext(enterprise) && shopifyHoldOn("embroidery");

export const shouldHideShopReauthenticate = (enterprise) =>
  isOmniCommerceContext(enterprise) && shopifyHoldOn("shopReauthenticate");

export const shouldHideOmniMycartAddShop = (enterprise) =>
  isOmniCommerceContext(enterprise) && shopifyHoldOn("mycartAddShop");

/** Omni print-shop admin (not a seller). Can add a card for COGS invoices. */
export const isOmniPrintShopAdmin = (enterprise, user) =>
  isOmniCommerceContext(enterprise) &&
  !!user?.isEnterpriseAdmin &&
  !user?.isProUser;

/**
 * Hide Riverr card-upload from Omni sellers. Print-shop admin is allowed.
 * Other brands: never extra-hide from this helper.
 */
export const shouldHideOmniSellerCardUpload = (enterprise, user) => {
  if (
    !isOmniCommerceContext(enterprise) ||
    !shopifyHoldOn("paymentMethodPrompts")
  ) {
    return false;
  }
  return !isOmniPrintShopAdmin(enterprise, user);
};

export const shouldHidePaymentMethodRequirement = (enterprise, user) => {
  if (isOmniPrintShopAdmin(enterprise, user)) {
    return (
      isBigfrogDomian ||
      isNetworkFranchiseMember(enterprise) ||
      !!enterprise?.hidePaymentMethodAlert ||
      !!user?.billingDisabled
    );
  }
  return (
    isBigfrogDomian ||
    isNetworkFranchiseMember(enterprise) ||
    shouldHideOffPlatformAppBilling(enterprise) ||
    !!enterprise?.hidePaymentMethodAlert ||
    !!user?.billingDisabled
  );
};

/**
 * SaaS subscription pricing (/pricing Free/Growth/Business) is for standalone
 * Riverr tenants. Big Frog HQ + franchise locations, OmniCommerce (Shopify
 * App Store listing), and any network franchise member never pick those
 * plans — hide the page and every CTA that leads there.
 *
 * Prefer enterprise checks over `isBigfrogDomian`: franchisees log in on
 * `{child}.myriverr.com`, so the HQ hostname check alone misses them. The
 * `bigfrog*` / OmniCommerce hostname heuristics cover the brief window
 * before enterprise loads so the pricing page never flashes on those portals.
 */
export const shouldHideSaaSPricing = (enterprise) => {
  if (
    isOmniCommerceContext(enterprise) &&
    shopifyHoldOn("saasPricingAndSubscriptions")
  ) {
    return true;
  }
  if (
    isBigfrogDomian ||
    isBigFrogEnterprise(enterprise) ||
    isNetworkFranchiseMember(enterprise)
  ) {
    return true;
  }
  if (typeof window !== "undefined") {
    const host = String(window.location.hostname || "").toLowerCase();
    if (host === "bigfrog.myriverr.com" || host.startsWith("bigfrog-")) {
      return true;
    }
    // House Label is Riverr's own demand brand enterprise. Skip the
    // SaaS pricing paywall so the admin opens straight into dashboard.
    if (host === "houselabel.myriverr.com") {
      return true;
    }
    if (
      (host === "omnicommerce.myriverr.com" ||
        host === "fabritech.myriverr.com") &&
      shopifyHoldOn("saasPricingAndSubscriptions")
    ) {
      return true;
    }
  }
  return false;
};

export const isUrbanprintshopDomian = (function () {
  return currentDomain === "app.urbanprintshopco.com";
})();

export const isRepyourbrandDomian = (function () {
  return currentDomain === "repyourbrand.myriverr.com";
})();

export const isEdgePrintedProductsDomain = (function () {
  return currentDomain === "app.edgeprintedproducts.com";
})();

export const isMerchfulDomain = (function () {
  return currentDomain === "app.merchful.com";
})();

export const isIcyStudiosDomain = (function () {
  return (
    currentDomain === "app.icystudios.com" ||
    currentDomain === "pod.icy.com"
  );
})();

export const isAnyMyRiverrDomain = (function () {
  return currentDomain.endsWith(".myriverr.com");
})();

// accounts.riverr.app is the dedicated founder signup + onboarding
// surface. Read by `Register/index.js` to pick the `AccountsRegister`
// variant, and by the onboarding wizard pages to render their layout.
// Distinct from `app.riverr.app` (the Pro dashboard) and
// `{tenant}.myriverr.com` (per-tenant white-label).
export const isAccountsRiverrDomain = (function () {
  return currentDomain === "accounts.riverr.app";
})();

export const domainName = (function () {
  let DN = currentDomain.split(".");
  DN = DN.length > 2 ? DN[1] : DN[0];
  return DN;
})();

export const isRiverrEnterprise = (enterpriseId) => {
  return enterpriseId === "cfbZh6XFBG3usCcUwpRE";
};

const BETA_DOMAINS = [
  "localhost",
  "riverr-enterprise-user.web.app",
  "app.bespokethreads.com",
  "repyourbrand.myriverr.com",
];

export const isBetaDomain = (function () {
  return BETA_DOMAINS.includes(currentDomain);
})();

const HOME_ICON_LINK = {
  "app.podhut.com": "//podhut.com",
  default: `//${window.location.host}`,
};

export const homeIconLink = (function () {
  return HOME_ICON_LINK[currentDomain] || HOME_ICON_LINK["default"];
})();

export const currencySymbol = (function () {
  let symbol = "$";

  if (isTeePrintCenterDomain) {
    symbol = "AUD";
  }

  return symbol;
})();

// Re-export file utility functions
export { sanitizeFileName, sanitizeFiles } from "./fileUtils";
